'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconCheck } from '@repo/svgs';

export interface Step {
  id: number;
  label: string;
  isOptional?: boolean;
}

// StepperIndicator component
export interface StepperIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number;
  unlocked?: boolean;
  active?: boolean;
  completed?: boolean;
}

export function StepperIndicator({
  index,
  unlocked = false,
  active = false,
  completed = false,
  className,
  ...rest
}: StepperIndicatorProps) {
  if (completed) {
    return (
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center',
          'bg-accent',
          className,
        )}
        {...rest}
      >
        <IconCheck className="w-3 h-3 icon-inverted" />
      </div>
    );
  }

  if (active) {
    return (
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center',
          'bg-accent',
          className,
        )}
        {...rest}
      >
        <span className="text-caption text-inverted text-center">{index}</span>
      </div>
    );
  }

  if (unlocked) {
    return (
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center',
          'bg-inverted-secondary',
          className,
        )}
        {...rest}
      >
        <span className="text-caption text-inverted text-center">{index}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-6 h-6 rounded-full flex items-center justify-center',
        'bg-tertiary',
        className,
      )}
      {...rest}
    >
      <span className="text-caption text-tertiary text-center">{index}</span>
    </div>
  );
}

// StepperNavCell component
export interface StepperNavCellProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  step: Step;
  active: boolean;
  completed: boolean;
  unlocked?: boolean;
  substepActive?: boolean;
  onStepChange?: (id: number) => void;
}

export function StepperNavCell({
  step,
  active,
  completed,
  unlocked,
  substepActive = false,
  onStepChange,
  className,
  ...rest
}: StepperNavCellProps) {
  const isDisabled = Boolean(rest.disabled);
  const effectiveUnlocked = Boolean(unlocked ?? (completed || active));
  const isClickable = Boolean(onStepChange && effectiveUnlocked && !isDisabled);
  const indicatorActive = !completed && (active || substepActive);

  const handleClick = () => {
    if (!isClickable || !onStepChange) return;
    onStepChange(step.id);
  };

  return (
    <button
      type="button"
      className={cn(
        // Base layout
        'flex items-center gap-2 rounded-xl px-2 py-3 w-full transition-colors',

        // Background colors
        active ? 'bg-active' : 'bg-primary',

        // Interactive states
        isClickable && 'cursor-pointer',
        isClickable && !active && 'hover:bg-primary-hover',
        isClickable && active && 'hover:bg-active-hover',
        !isClickable && 'cursor-default',
        isDisabled && 'opacity-50 cursor-not-allowed',

        // Custom classes
        className,
      )}
      onClick={handleClick}
      aria-current={active ? 'step' : undefined}
      aria-disabled={!effectiveUnlocked || isDisabled || undefined}
      {...rest}
    >
      <StepperIndicator
        index={step.id}
        unlocked={effectiveUnlocked}
        active={indicatorActive}
        completed={completed}
      />
      <span
        className={cn(
          active && 'text-body-2-high text-accent',
          !active && effectiveUnlocked && 'text-body-2 text-primary',
          !active && !effectiveUnlocked && 'text-body-2 text-tertiary',
        )}
      >
        {step.label}
      </span>
    </button>
  );
}

// Main Stepper component
export interface StepperProps extends React.HTMLAttributes<HTMLElement> {
  steps: Step[];
  currentStepId: number;
  onStepChange?: (id: number) => void;
  isStepCompleted?: (stepId: number) => boolean;
  /**
   * Unlocked means a step can be navigated to (independent of completed).
   * If not provided, defaults to allowing navigation to current and previous steps.
   */
  isStepUnlocked?: (stepId: number) => boolean;
  /**
   * Allows highlighting the indicator when a substep inside a step is active,
   * without making the whole step row active.
   */
  isSubstepActive?: (stepId: number) => boolean;
}

export function Stepper({
  steps,
  currentStepId,
  onStepChange,
  isStepCompleted,
  isStepUnlocked,
  isSubstepActive,
  className,
  ...rest
}: StepperProps) {
  return (
    <nav aria-label="Steps" className={cn('flex flex-col gap-3', className)} {...rest}>
      {steps.map(step => {
        const active = step.id === currentStepId;
        // Use custom completion check if provided, otherwise fall back to default logic
        const completed = isStepCompleted ? isStepCompleted(step.id) : step.id < currentStepId;
        const substepActive = isSubstepActive ? isSubstepActive(step.id) : false;
        const unlockedBase = (() => {
          if (isStepUnlocked) return isStepUnlocked(step.id);

          if (isStepCompleted) {
            // Consider "prior steps" as those with a lower id.
            const priorSteps = steps.filter(s => s.id < step.id);
            return priorSteps.length === 0 ? true : priorSteps.every(s => isStepCompleted(s.id));
          }

          return step.id <= currentStepId;
        })();

        const unlocked = Boolean(unlockedBase || active || completed || substepActive);

        return (
          <StepperNavCell
            key={step.id}
            step={step}
            active={active}
            completed={completed}
            unlocked={unlocked}
            substepActive={substepActive}
            {...(onStepChange && { onStepChange })}
          />
        );
      })}
    </nav>
  );
}

export default Stepper;
